import { motion } from 'framer-motion'
import type { BaccaratBetType } from '@/types'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/utils'

interface BetOptionProps {
  type: BaccaratBetType
  label: string
  payout: string
  betAmount: number
  isSelected: boolean
  disabled?: boolean
  onClick: () => void
}

function BetOption({
  type,
  label,
  payout,
  betAmount,
  isSelected,
  disabled,
  onClick,
}: BetOptionProps) {
  const colors = {
    player: {
      bg: 'from-blue-700 to-blue-900',
      border: 'border-blue-500',
      selected: 'border-casino-gold shadow-gold',
    },
    banker: {
      bg: 'from-red-700 to-red-900',
      border: 'border-red-500',
      selected: 'border-casino-gold shadow-gold',
    },
    tie: {
      bg: 'from-green-700 to-green-900',
      border: 'border-green-500',
      selected: 'border-casino-gold shadow-gold',
    },
  }

  const color = colors[type]

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.02, y: -2 } : undefined}
      whileTap={!disabled ? { scale: 0.98 } : undefined}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'p-4 rounded-xl text-center min-w-[120px] transition-all duration-200',
        `bg-gradient-to-br ${color.bg}`,
        'border-2',
        isSelected ? color.selected : color.border,
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && 'cursor-pointer'
      )}
    >
      <h4 className="font-cinzel text-white text-sm font-bold mb-1">{label}</h4>
      <p className="text-xs text-white/70 mb-2">{payout}</p>
      {betAmount > 0 && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="text-casino-gold font-bold"
        >
          {formatCurrency(betAmount)}
        </motion.div>
      )}
    </motion.button>
  )
}

interface BetAreasProps {
  selectedBet: BaccaratBetType | null
  betAmount: number
  disabled?: boolean
  onSelectBet: (type: BaccaratBetType) => void
}

export function BetAreas({
  selectedBet,
  betAmount,
  disabled,
  onSelectBet,
}: BetAreasProps) {
  return (
    <div className="flex justify-center gap-4 p-4">
      <BetOption
        type="player"
        label="JUGADOR"
        payout="Paga 1:1"
        betAmount={selectedBet === 'player' ? betAmount : 0}
        isSelected={selectedBet === 'player'}
        disabled={disabled}
        onClick={() => onSelectBet('player')}
      />

      <BetOption
        type="tie"
        label="EMPATE"
        payout="Paga 8:1"
        betAmount={selectedBet === 'tie' ? betAmount : 0}
        isSelected={selectedBet === 'tie'}
        disabled={disabled}
        onClick={() => onSelectBet('tie')}
      />

      <BetOption
        type="banker"
        label="BANCA"
        payout="Paga 0.95:1"
        betAmount={selectedBet === 'banker' ? betAmount : 0}
        isSelected={selectedBet === 'banker'}
        disabled={disabled}
        onClick={() => onSelectBet('banker')}
      />
    </div>
  )
}
